// shared-lib/kitchen-assembly/styling/unchosenBacksplash.js
//
// A backsplash band drawn before anybody has picked a tile for it (user,
// 2026-10-20, item #42 of a contractor call). Applying a new kitchen layout
// draws the kitchen's bands in a DEFAULT tile, so the kitchen reads as having
// a backsplash in 3D and on the elevations, and marks each one "not chosen":
// the estimate bills it as an allowance until a tile is picked.
//
// The mark is a FLAG on the band (`unchosen: true`), never read off which
// texture it wears. The default tile is a real catalog tile, priced as a real
// pick wherever someone did choose it — exactly the trap a generic id falls
// into elsewhere (`fixture-types/productRepresentation`), and the reason the
// reader and the clearer live here side by side, as they do there.
//
// Who clears it: everything that PICKS a tile. `retextureBacksplashWalls`
// (the Fixtures & Finishes pick, in place), `apply_backsplash_texture` and
// `style_kitchen` (both build fresh bands, which carry no flag — the tool
// carries it over for the kind it was NOT asked to tile), and Edit wall on a
// band whose tile it changes (the web's `wallRegionFormat` / `wallRegionEdits`
// keep the flag only until a pick changes the tile, and `groupedRegionSync`
// clears it on the pieces that pick reaches on other walls). What KEEPS it:
// `rebuildGeneratedBacksplash`, which re-fits the bands after a cabinet moves
// and carries the flag per kind, like the tile — and Edit wall on any save
// that leaves the band's tile alone.

/** The field that says a band's tile has not been chosen. */
const UNCHOSEN_FIELD = 'unchosen';

/**
 * The tile a newly laid-out kitchen's bands are drawn in: a white 3" x 6"
 * polished marble subway ("Greecian White Marble Tile", user 2026-10-26; it
 * was the Pier White ceramic subway, `hd_307829619`). Only how the band
 * LOOKS — the estimate prices a not-chosen band as the allowance, never as
 * this product. Must exist in `unifiedTextures` in every environment the
 * layout apply runs in. Not the 6" x 12" MSI tile of the same name
 * (`hd_205762432`), which is the default marble FLOOR.
 */
const DEFAULT_BACKSPLASH_TILE_ID = 'hd_100664302';

/**
 * How the default bands are drawn — what a rebuild falls back to when there
 * is nothing to read back off the wall: one tile behind the counter and the
 * range alike, 18" over the counter where nothing hangs above it and up to
 * the uppers where they do, and up to the hood behind the range.
 */
const DEFAULT_BACKSPLASH_CONFIG = Object.freeze({
  resolvedStyle: null,
  combineBacksplash: true,
  counterBacksplash: { lowProfile: false },
  stoveBacksplash: { toHood: true },
});

/** Is this region a backsplash band whose tile nobody has chosen yet? */
const isUnchosenBacksplash = (region) =>
  region?.surfaceType === 'backsplash' && region[UNCHOSEN_FIELD] === true;

/** A copy of `region` without the flag — the band's tile is now chosen. */
function clearUnchosenBacksplash(region) {
  if (!region || !(UNCHOSEN_FIELD in region)) return region;
  const out = { ...region };
  delete out[UNCHOSEN_FIELD];
  return out;
}

module.exports = {
  UNCHOSEN_FIELD,
  DEFAULT_BACKSPLASH_TILE_ID,
  DEFAULT_BACKSPLASH_CONFIG,
  isUnchosenBacksplash,
  clearUnchosenBacksplash,
};
